/**
 * PDF-Based FOIA Redaction Editor
 * Displays original PDF with overlaid redactions, ensuring visual consistency with final output
 */

import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { 
  XMarkIcon,
  EyeSlashIcon, 
  CheckIcon, 
  XMarkIcon as RejectIcon,
  EyeIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  UserIcon,
  Bars3Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  ArrowDownTrayIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import ExemptionSelector from './ExemptionSelector';
import { generateRedactedPDF, getFOIARule } from '../../services/api';
import { PDFJS_DOCUMENT_OPTIONS } from '../../utils/pdfjsOptions';

// Set up PDF.js worker - use local worker file
pdfjs.GlobalWorkerOptions.workerSrc = '/static/js/pdf.worker.min.js';

const PDFRedactionEditor = ({
  pdfFile = null,
  pdfUrl = null,
  initialRedactions = [],
  onRedactionsChange = () => {},
  onSave = () => {},
  onClose = () => {},
  showApprovalWorkflow = false,
  agency = "",
  requesterInterest = ""
}) => {
  // PDF state
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.2);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageHeight, setPageHeight] = useState(792); // Track page height for coordinate conversion
  
  // Redaction state
  const [redactions, setRedactions] = useState(initialRedactions);
  const [selectedRedactionIndex, setSelectedRedactionIndex] = useState(null);
  const [currentWorkflowIndex, setCurrentWorkflowIndex] = useState(0);
  
  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pdfPreviewMode, setPdfPreviewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Rule modal state
  const [selectedRule, setSelectedRule] = useState(null);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [loadingRule, setLoadingRule] = useState(false);
  
  // Selection state for creating new redactions
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [selectedExemptionForCreation, setSelectedExemptionForCreation] = useState(null);
  
  // Use ref to track current selection end during drag
  const currentSelectionEnd = useRef(null);
  
  // Resize state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeData, setResizeData] = useState(null);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Update parent when redactions change
  useEffect(() => {
    onRedactionsChange(redactions);
  }, [redactions, onRedactionsChange]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Handle resize move
  const handleResizeMove = (e) => {
    if (!isResizing || !resizeData) return;

    e.preventDefault();
    const { redactionIndex, bboxIndex = 0, corner, startX, startY, originalBbox, containerRect } = resizeData;

    const deltaX = (e.clientX - startX) / scale;
    const deltaY = (e.clientY - startY) / scale;

    let [x1, y1, x2, y2] = originalBbox;

    // Adjust bbox based on corner being dragged
    switch (corner) {
      case 'nw':
        x1 += deltaX;
        y1 += deltaY;
        break;
      case 'ne':
        x2 += deltaX;
        y1 += deltaY;
        break;
      case 'sw':
        x1 += deltaX;
        y2 += deltaY;
        break;
      case 'se':
        x2 += deltaX;
        y2 += deltaY;
        break;
      default:
        break;
    }

    // Ensure minimum size and correct order
    const minSize = 10;
    if (Math.abs(x2 - x1) < minSize) return;
    if (Math.abs(y2 - y1) < minSize) return;

    const newBbox = [
      Math.min(x1, x2),
      Math.min(y1, y2),
      Math.max(x1, x2),
      Math.max(y1, y2)
    ];

    // Update the specific bbox in the multi-bbox redaction
    updateRedactionBbox(redactionIndex, bboxIndex, newBbox);
  };

  // Handle resize end
  const handleResizeEnd = () => {
    setIsResizing(false);
    setResizeData(null);
  };

  // Handle selection for creating new redactions
  const handleSelectionStart = (e) => {
    if (pdfPreviewMode || showApprovalWorkflow || !selectedExemptionForCreation) {
      return;
    }
    
    // Don't trigger if clicking on existing redactions
    if (e.target.closest('.redaction-overlay-pending, .redaction-overlay-approved, .redaction-overlay-rejected')) {
      return;
    }
    
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) {
      return;
    }
    
    const x = e.clientX - containerRect.left;
    const y = e.clientY - containerRect.top;
    
    setIsSelecting(true);
    setSelectionStart({ x, y });
    setSelectionEnd({ x, y });
    currentSelectionEnd.current = { x, y };
    
    e.preventDefault();
    e.stopPropagation();
  };

  const handleSelectionMove = (e) => {
    if (!isSelecting || !selectionStart) {
      return;
    }
    
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    
    const x = e.clientX - containerRect.left;
    const y = e.clientY - containerRect.top;
    
    setSelectionEnd({ x, y });
    currentSelectionEnd.current = { x, y };
  };

  const handleSelectionEnd = (e) => {
    const currentEnd = currentSelectionEnd.current;
    
    if (!isSelecting || !selectionStart || !currentEnd || !selectedExemptionForCreation) {
      setIsSelecting(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      currentSelectionEnd.current = null;
      return;
    }
    
    // Check if selection is large enough using the ref value
    const width = Math.abs(currentEnd.x - selectionStart.x);
    const height = Math.abs(currentEnd.y - selectionStart.y);
    
    if (width < 20 || height < 10) {
      // Selection too small, cancel
      setIsSelecting(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      currentSelectionEnd.current = null;
      return;
    }
    
    // Create redaction automatically with pre-selected exemption
    handleCreateRedactionWithCoords(selectedExemptionForCreation, selectionStart, currentEnd);
  };

  // Mouse event listeners for resize and drag
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing) {
        handleResizeMove(e);
      } else if (isSelecting) {
        handleSelectionMove(e);
      }
    };

    const handleMouseUp = (e) => {
      if (isResizing) {
        handleResizeEnd();
      } else if (isSelecting) {
        handleSelectionEnd(e);
      }
    };

    if (isResizing || isSelecting) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, isSelecting]);

  // PDF loading handlers
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfDoc({ numPages }); // Store document for page size queries
    setError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF loading error:', error);
    setError(`Failed to load PDF: ${error.message}`);
  };

  // Convert text coordinates to PDF coordinates
  const convertTextToPDFCoordinates = (textRedaction, pageHeightOverride = null) => {
    const currentPageHeight = pageHeightOverride || pageHeight;
    // Use bbox coordinates if available from backend (these are the actual PDF coordinates used in redaction)
    if (textRedaction.bbox && Array.isArray(textRedaction.bbox) && textRedaction.bbox.length === 4) {
      const [x1, y1, x2, y2] = textRedaction.bbox;
      
      // Try using coordinates directly without Y-flip
      
      return {
        x: Math.max(0, x1 * scale),
        y: Math.max(0, y1 * scale),  // Use original Y-coordinate
        width: Math.max(50, (x2 - x1) * scale),
        height: Math.max(15, (y2 - y1) * scale),
        page: textRedaction.page || currentPage
      };
    }
    
    // If this is a coordinate_redaction from the backend, it should have accurate coordinates
    if (textRedaction.text && typeof textRedaction.exemption === 'string' && textRedaction.page) {
      // This is likely a coordinate redaction from the backend with proper PDF coordinates
      const estimatedX = 60;
      const estimatedY = 50;
      const estimatedWidth = Math.min(300, textRedaction.text ? textRedaction.text.length * 6 : 150);
      const estimatedHeight = 20;
      
      return {
        x: estimatedX,
        y: estimatedY,
        width: estimatedWidth,
        height: estimatedHeight,
        page: textRedaction.page || currentPage
      };
    }
    
    // Fallback estimation with spread positioning
    const redactionIndex = redactions.indexOf(textRedaction);
    const estimatedY = 50 + (redactionIndex * 30); // Spread vertically
    const estimatedHeight = Math.max(15, Math.min(25, textRedaction.original_text ? textRedaction.original_text.length * 0.2 : 20));
    const estimatedWidth = Math.min(300, textRedaction.original_text ? textRedaction.original_text.length * 6 : 150);
    
    return {
      x: 60,
      y: estimatedY,
      width: estimatedWidth,
      height: estimatedHeight,
      page: textRedaction.page || currentPage
    };
  };

  // Render redaction overlays on PDF
  const renderRedactionOverlays = () => {
    if (!redactions || redactions.length === 0) return null;

    // Debug page filtering and preserve original indices
    const currentPageRedactions = redactions
      .map((redaction, originalIndex) => ({ redaction, originalIndex }))
      .filter(({ redaction }) => redaction.page === currentPage);

    return currentPageRedactions
      .flatMap(({ redaction, originalIndex }, pageIndex) => {
        const isSelected = selectedRedactionIndex === originalIndex;
        const statusClass = `redaction-overlay-${redaction.status || 'pending'}`;
        const exemptionClass = `exemption-${(redaction.exemption || '(b)(1)').replace(/[()]/g, '').toLowerCase()}`;

        // Support both single bbox and multiple bboxes
        const bboxes = redaction.bboxes || (redaction.bbox ? [redaction.bbox] : []);

        if (bboxes.length === 0) {
          // Fallback to text-based coordinates if no bbox available
          const coords = convertTextToPDFCoordinates(redaction);
          bboxes.push([
            coords.x / scale,
            coords.y / scale,
            (coords.x + coords.width) / scale,
            (coords.y + coords.height) / scale
          ]);
        }

        // Render each bbox as a separate visual element, but all linked to the same redaction
        return bboxes.map((bbox, bboxIndex) => {
          const coords = {
            x: bbox[0] * scale,
            y: bbox[1] * scale,
            width: (bbox[2] - bbox[0]) * scale,
            height: (bbox[3] - bbox[1]) * scale
          };

          return (
            <div
              key={`${redaction.id || originalIndex}-${bboxIndex}`}
              className={`absolute cursor-pointer transition-all duration-200 ${statusClass} ${exemptionClass} ${
                isSelected ? 'ring-2 ring-blue-500 ring-offset-1' : ''
              }`}
              style={{
                left: coords.x,
                top: coords.y,
                width: coords.width,
                height: coords.height,
                backgroundColor: pdfPreviewMode
                  ? '#000000'
                  : redaction.status === 'approved'
                    ? 'rgba(34, 197, 94, 0.3)'
                    : redaction.status === 'rejected'
                      ? 'rgba(239, 68, 68, 0.3)'
                      : 'rgba(59, 130, 246, 0.3)',
                border: pdfPreviewMode
                  ? 'none'
                  : isSelected
                    ? '2px solid #3b82f6'
                    : redaction.status === 'approved'
                      ? '1px solid #22c55e'
                      : redaction.status === 'rejected'
                        ? '1px solid #ef4444'
                        : '1px solid #3b82f6',
                borderRadius: pdfPreviewMode ? '0' : '2px',
                zIndex: isSelected ? 20 : 10
              }}
              onClick={() => handleRedactionClick(originalIndex)}
              title={`${redaction.exemption || '(b)(1)'} - ${redaction.status || 'pending'} (${Math.round((redaction.confidence || 0) * 100)}%) ${bboxes.length > 1 ? `[Part ${bboxIndex + 1}/${bboxes.length}]` : ''}`}
            >
              {/* Resize handles when selected - only show on the first bbox to avoid clutter */}
              {isSelected && !pdfPreviewMode && bboxIndex === 0 && (
                <>
                  <div
                    className="absolute -left-1 -top-1 w-3 h-3 bg-blue-500 rounded-full cursor-nw-resize"
                    onMouseDown={(e) => handleResizeStart(e, originalIndex, 'nw', bboxIndex)}
                  />
                  <div
                    className="absolute -right-1 -top-1 w-3 h-3 bg-blue-500 rounded-full cursor-ne-resize"
                    onMouseDown={(e) => handleResizeStart(e, originalIndex, 'ne', bboxIndex)}
                  />
                  <div
                    className="absolute -left-1 -bottom-1 w-3 h-3 bg-blue-500 rounded-full cursor-sw-resize"
                    onMouseDown={(e) => handleResizeStart(e, originalIndex, 'sw', bboxIndex)}
                  />
                  <div
                    className="absolute -right-1 -bottom-1 w-3 h-3 bg-blue-500 rounded-full cursor-se-resize"
                    onMouseDown={(e) => handleResizeStart(e, originalIndex, 'se', bboxIndex)}
                  />
                </>
              )}

              {/* Redaction label - only show on first bbox to avoid clutter */}
              {!pdfPreviewMode && bboxIndex === 0 && (
                <div className="absolute -top-6 left-0 text-xs bg-black text-white px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                  {redaction.exemption || '(b)(1)'}
                  {bboxes.length > 1 && (
                    <span className="ml-1 text-gray-300">({bboxes.length} parts)</span>
                  )}
                </div>
              )}
            </div>
          );
        });
      });
  };

  // Handle redaction selection
  const handleRedactionClick = (index) => {
    setSelectedRedactionIndex(index);
    setSidebarOpen(true);
  };

  // Handle resize start
  const handleResizeStart = (e, redactionIndex, corner, bboxIndex = 0) => {
    e.stopPropagation();
    e.preventDefault();

    const redaction = redactions[redactionIndex];
    const containerRect = containerRef.current?.getBoundingClientRect();

    // Support both single bbox and multi-bbox redactions
    const bboxes = redaction.bboxes || (redaction.bbox ? [redaction.bbox] : []);
    if (!containerRect || bboxes.length === 0 || !bboxes[bboxIndex]) return;

    setIsResizing(true);
    setResizeData({
      redactionIndex,
      bboxIndex,
      corner,
      startX: e.clientX,
      startY: e.clientY,
      originalBbox: [...bboxes[bboxIndex]],
      containerRect
    });
  };

  // Create multi-line bboxes from a selection rectangle
  const createMultiLineBboxes = (startPos, endPos) => {
    const minX = Math.min(startPos.x, endPos.x) / scale;
    const maxX = Math.max(startPos.x, endPos.x) / scale;
    const minY = Math.min(startPos.y, endPos.y) / scale;
    const maxY = Math.max(startPos.y, endPos.y) / scale;

    const selectionHeight = maxY - minY;
    const averageLineHeight = 16 / scale; // Estimate typical line height in PDF units

    // If selection is tall enough to span multiple lines, create per-line bboxes
    if (selectionHeight > averageLineHeight * 1.5) {
      const bboxes = [];
      const numLines = Math.ceil(selectionHeight / averageLineHeight);

      for (let i = 0; i < numLines; i++) {
        const lineY = minY + (i * averageLineHeight);
        const lineMaxY = Math.min(lineY + averageLineHeight, maxY);

        // Create bbox for this line
        // For first and last lines, we might want to adjust X coordinates
        // to better match text boundaries, but for now use full width
        bboxes.push([
          minX,
          lineY,
          maxX,
          lineMaxY
        ]);
      }

      return bboxes;
    } else {
      // Single line selection
      return [[minX, minY, maxX, maxY]];
    }
  };

  // Create new redaction with explicit coordinates
  const handleCreateRedactionWithCoords = (exemption, startPos, endPos) => {
    if (!startPos || !endPos) {
      return;
    }

    // Create multi-line bboxes if the selection spans multiple lines
    const bboxes = createMultiLineBboxes(startPos, endPos);

    const newRedaction = {
      id: `redaction-${Date.now()}`,
      page: currentPage,
      bboxes: bboxes,
      // Keep legacy bbox for backwards compatibility (use the overall bounding box)
      bbox: [
        Math.min(startPos.x, endPos.x) / scale,
        Math.min(startPos.y, endPos.y) / scale,
        Math.max(startPos.x, endPos.x) / scale,
        Math.max(startPos.y, endPos.y) / scale
      ],
      original_text: `Manual redaction (${bboxes.length} ${bboxes.length === 1 ? 'part' : 'parts'})`,
      exemption,
      status: 'pending',
      confidence: 0.9,
      justification: 'Manually created redaction',
      rule_id: 'MANUAL'
    };

    setRedactions(prev => [...prev, newRedaction]);
    setSelectedRedactionIndex(redactions.length);

    // Clear selection and exemption
    setSelectionStart(null);
    setSelectionEnd(null);
    setIsSelecting(false);
    setSelectedExemptionForCreation(null);
    currentSelectionEnd.current = null;
  };

  // Create new redaction from selection (legacy method)
  const handleCreateRedaction = (exemption = '(b)(1)') => {
    if (!selectionStart || !selectionEnd) {
      return;
    }

    handleCreateRedactionWithCoords(exemption, selectionStart, selectionEnd);
  };

  // Start creation mode with selected exemption
  const startRedactionCreation = (exemption) => {
    setSelectedExemptionForCreation(exemption);
  };

  // Cancel creation mode
  const cancelRedactionCreation = () => {
    setSelectedExemptionForCreation(null);
    setSelectionStart(null);
    setSelectionEnd(null);
    setIsSelecting(false);
    currentSelectionEnd.current = null;
  };

  // Update redaction
  const updateRedaction = (index, updates) => {
    setRedactions(prev => prev.map((r, i) => i === index ? { ...r, ...updates } : r));
  };

  // Update specific bbox within a multi-bbox redaction
  const updateRedactionBbox = (redactionIndex, bboxIndex, newBbox) => {
    setRedactions(prev => prev.map((redaction, i) => {
      if (i !== redactionIndex) return redaction;

      // Handle both single bbox and multi-bbox redactions
      const bboxes = redaction.bboxes || (redaction.bbox ? [redaction.bbox] : []);

      if (bboxIndex >= bboxes.length) return redaction;

      const updatedBboxes = [...bboxes];
      updatedBboxes[bboxIndex] = newBbox;

      // Return updated redaction with new bboxes array
      return {
        ...redaction,
        bboxes: updatedBboxes,
        // Remove legacy bbox field when using bboxes
        bbox: undefined
      };
    }));
  };

  // Remove redaction
  const removeRedaction = (index) => {
    setRedactions(prev => prev.filter((_, i) => i !== index));
    setSelectedRedactionIndex(null);
  };

  // Download redacted PDF
  const downloadRedactedPDF = async () => {
    setLoading(true);
    try {
      const response = await generateRedactedPDF({
        pdf_file: pdfFile,
        redactions: redactions,
        agency: agency,
        requester_interest: requesterInterest
      });

      // The response is already a blob from the API
      const blob = response.data || response; // Handle different response formats
      if (!(blob instanceof Blob)) {
        console.error('Expected blob response, got:', typeof blob, blob);
        throw new Error('Invalid PDF response format');
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'redacted_document.pdf';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download redacted PDF:', error);
      setError('Failed to generate redacted PDF');
    } finally {
      setLoading(false);
    }
  };

  // Workflow navigation
  const goToNextRedaction = () => {
    if (currentWorkflowIndex < redactions.length - 1) {
      setCurrentWorkflowIndex(currentWorkflowIndex + 1);
      setSelectedRedactionIndex(currentWorkflowIndex + 1);
    }
  };

  const goToPreviousRedaction = () => {
    if (currentWorkflowIndex > 0) {
      setCurrentWorkflowIndex(currentWorkflowIndex - 1);
      setSelectedRedactionIndex(currentWorkflowIndex - 1);
    }
  };

  // Rule modal handlers
  const handleRuleClick = async (ruleId) => {
    if (!ruleId) return;
    
    setLoadingRule(true);
    try {
      const ruleData = await getFOIARule(ruleId);
      setSelectedRule(ruleData);
      setRuleModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch rule:', err);
      // Could add error toast here
    } finally {
      setLoadingRule(false);
    }
  };

  const closeRuleModal = () => {
    setRuleModalOpen(false);
    setSelectedRule(null);
  };

  const copyRuleJSON = () => {
    if (selectedRule) {
      navigator.clipboard.writeText(JSON.stringify(selectedRule, null, 2));
      // Could add a toast notification here
    }
  };

  // Helper function for exemption badge colors
  const getExemptionBadgeColor = (exemption) => {
    if (!exemption || typeof exemption !== 'string') {
      return 'bg-gray-100 text-gray-800';
    }
    
    const colors = {
      'b1': 'bg-red-100 text-red-800',
      'b2': 'bg-orange-100 text-orange-800',
      'b3': 'bg-yellow-100 text-yellow-800',
      'b4': 'bg-green-100 text-green-800',
      'b5': 'bg-blue-100 text-blue-800',
      'b6': 'bg-indigo-100 text-indigo-800',
      'b7': 'bg-purple-100 text-purple-800'
    };
    return colors[exemption.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  // Helper function to parse multiple rule IDs from combined strings
  const parseRuleIds = (ruleIdString) => {
    if (!ruleIdString || typeof ruleIdString !== 'string') {
      return [];
    }
    
    // Split by common separators and clean up
    const rules = ruleIdString
      .split(/[\+\,\;\|]/)  // Split on +, ,, ;, |
      .map(rule => rule.trim())
      .filter(rule => rule && rule !== '' && rule !== 'None' && !rule.includes('directly applicable'))
      .map(rule => {
        // Clean up common patterns
        if (rule.includes(' + ')) {
          return rule.split(' + ')[0].trim();
        }
        return rule;
      })
      .filter((rule, index, array) => array.indexOf(rule) === index); // Remove duplicates
    
    return rules;
  };

  const selectedRedaction = selectedRedactionIndex !== null ? redactions[selectedRedactionIndex] : null;
  const currentRedaction = showApprovalWorkflow ? redactions[currentWorkflowIndex] : null;
  const activeRedaction = showApprovalWorkflow ? currentRedaction : selectedRedaction;
  const activeIndex = showApprovalWorkflow ? currentWorkflowIndex : selectedRedactionIndex;

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
        <div className="bg-white p-6 rounded-lg max-w-md">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Error Loading PDF</h2>
          <p className="text-gray-700 mb-4">{error}</p>
          <button onClick={onClose} className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex bg-black bg-opacity-75 backdrop-blur-sm">
      
      {/* Main content area */}
      <div className="flex-1 flex flex-col">
        
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-gray-900">PDF Redaction Editor</h1>
            <div className="text-sm text-gray-600">
              {redactions.length} redaction{redactions.length !== 1 ? 's' : ''}
              {numPages && ` • Page ${currentPage} of ${numPages}`}
            </div>
            {showApprovalWorkflow && (
              <div className="text-sm text-blue-600 font-medium">
                Step {currentWorkflowIndex + 1} of {redactions.length}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Zoom controls */}
            <div className="flex items-center gap-1 mr-2">
              <button
                onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
                className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              >
                <MagnifyingGlassMinusIcon className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-600 px-2">{Math.round(scale * 100)}%</span>
              <button
                onClick={() => setScale(s => Math.min(2.0, s + 0.1))}
                className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              >
                <MagnifyingGlassPlusIcon className="h-4 w-4" />
              </button>
            </div>
            
            <button
              onClick={() => setPdfPreviewMode(!pdfPreviewMode)}
              className={`
                inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md transition-colors
                ${pdfPreviewMode
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              {pdfPreviewMode ? (
                <>
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  Edit Mode
                </>
              ) : (
                <>
                  <EyeIcon className="h-4 w-4 mr-2" />
                  Preview Mode
                </>
              )}
            </button>
            
            <button
              onClick={downloadRedactedPDF}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 text-sm font-medium bg-green-600 text-white border border-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              {loading ? 'Generating...' : 'Download PDF'}
            </button>
            
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            >
              {sidebarOpen ? <ChevronRightIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Close editor (Esc)"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* PDF display area */}
        <div className="flex-1 overflow-auto bg-gray-100">
          <div className="flex justify-center p-4">
            <div 
              ref={containerRef} 
              className="relative bg-white shadow-lg"
              onMouseDown={handleSelectionStart}
              style={{ 
                userSelect: 'none',
                cursor: selectedExemptionForCreation ? 'crosshair' : 'default'
              }}
            >
              <Document
                file={pdfFile || pdfUrl}
                options={PDFJS_DOCUMENT_OPTIONS}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                }
              >
                <Page
                  pageNumber={currentPage}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  canvasRef={canvasRef}
                  onLoadSuccess={(page) => {
                    // Get actual page dimensions for accurate coordinate mapping
                    setPageHeight(page.originalHeight || 792);
                  }}
                />
                
                {/* Redaction overlays */}
                {renderRedactionOverlays()}
                
                {/* Selection overlay for creating new redactions */}
                {isSelecting && selectionStart && currentSelectionEnd.current && (
                  <div
                    className="absolute border-2 border-dashed border-blue-500 bg-blue-500 bg-opacity-20 pointer-events-none"
                    style={{
                      left: Math.min(selectionStart.x, currentSelectionEnd.current.x),
                      top: Math.min(selectionStart.y, currentSelectionEnd.current.y),
                      width: Math.abs(currentSelectionEnd.current.x - selectionStart.x),
                      height: Math.abs(currentSelectionEnd.current.y - selectionStart.y)
                    }}
                  />
                )}
              </Document>
              
              {/* Page navigation */}
              {numPages > 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-black bg-opacity-75 text-white px-4 py-2 rounded-lg">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                  </button>
                  <span className="text-sm">
                    {currentPage} / {numPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                    disabled={currentPage === numPages}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowRightIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Workflow footer */}
        {showApprovalWorkflow && redactions.length > 0 && (
          <div className="bg-white border-t border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={goToPreviousRedaction}
                  disabled={currentWorkflowIndex === 0}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Previous
                </button>
                
                <div className="w-64 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${((currentWorkflowIndex + 1) / redactions.length) * 100}%` }}
                  />
                </div>
                
                <button
                  onClick={goToNextRedaction}
                  disabled={currentWorkflowIndex >= redactions.length - 1}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ArrowRightIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    updateRedaction(currentWorkflowIndex, { status: 'approved' });
                    // Auto-advance to next redaction after approval
                    if (currentWorkflowIndex < redactions.length - 1) {
                      setTimeout(() => {
                        goToNextRedaction();
                      }, 100); // Small delay for visual feedback
                    }
                  }}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium bg-green-600 text-white border border-green-600 rounded-md hover:bg-green-700 transition-colors"
                >
                  <CheckIcon className="h-4 w-4 mr-1" />
                  Approve
                </button>

                <button
                  onClick={() => {
                    updateRedaction(currentWorkflowIndex, { status: 'rejected' });
                    // Auto-advance to next redaction after rejection
                    if (currentWorkflowIndex < redactions.length - 1) {
                      setTimeout(() => {
                        goToNextRedaction();
                      }, 100); // Small delay for visual feedback
                    }
                  }}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium bg-red-600 text-white border border-red-600 rounded-md hover:bg-red-700 transition-colors"
                >
                  <RejectIcon className="h-4 w-4 mr-1" />
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sidebar */}
      {sidebarOpen && (
        <div className="w-[28rem] bg-white flex flex-col">
          
          {/* Sidebar header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {activeRedaction ? 'Redaction Details' : 'All Redactions'}
              </h2>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Sidebar content */}
          <div className="flex-1 overflow-auto">
            
            {/* Active redaction details */}
            {activeRedaction && (
              <div className="p-6 border-b border-gray-200">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Selected Text
                    </label>
                    <div className="p-3 bg-gray-50 border rounded text-sm">
                      "{activeRedaction.original_text}"
                    </div>
                  </div>
                  
                  <ExemptionSelector
                    exemption={activeRedaction.exemption}
                    subExemption={activeRedaction.subExemption}
                    onExemptionChange={(exemption) => updateRedaction(activeIndex, { exemption })}
                    onSubExemptionChange={(subExemption) => updateRedaction(activeIndex, { subExemption })}
                    showDescription={true}
                  />
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={activeRedaction.status || 'pending'}
                      onChange={(e) => updateRedaction(activeIndex, { status: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="modified">Modified</option>
                    </select>
                  </div>
                  
                  {activeRedaction.confidence && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        AI Confidence
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="text-lg font-semibold">
                          {Math.round(activeRedaction.confidence * 100)}%
                        </div>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full"
                            style={{ width: `${activeRedaction.confidence * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Rule ID and Citation */}
                  {activeRedaction.rule_id && activeRedaction.rule_id !== 'GENERAL' && activeRedaction.rule_id !== 'MANUAL' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        FOIA Rule Citation{parseRuleIds(activeRedaction.rule_id).length > 1 ? 's' : ''}
                      </label>
                      <div className="space-y-2">
                        {parseRuleIds(activeRedaction.rule_id).map((ruleId, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded">
                            <span className="text-sm font-mono text-blue-800">{ruleId}</span>
                            <button
                              onClick={() => handleRuleClick(ruleId)}
                              className="flex items-center text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                              disabled={loadingRule}
                            >
                              <LinkIcon className="w-4 h-4 mr-1" />
                              View Rule
                              {loadingRule && <span className="ml-1 text-xs">(loading...)</span>}
                            </button>
                          </div>
                        ))}
                        {parseRuleIds(activeRedaction.rule_id).length === 0 && (
                          <div className="p-2 bg-orange-50 border border-orange-200 rounded">
                            <span className="text-sm text-orange-800">{activeRedaction.rule_id}</span>
                            <span className="text-xs text-orange-600 ml-2">(No specific rule available)</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Exemption Justification with Model Reasoning */}
                  {activeRedaction.justification && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Exemption Justification & Model Reasoning
                      </label>
                      {(() => {
                        const justification = activeRedaction.justification;
                        const warningMatch = justification.match(/\[WARNING: ([^\]]+)\]/);
                        const isGeneralExemption = !activeRedaction.rule_id || activeRedaction.rule_id === 'GENERAL' || activeRedaction.rule_id === '';
                        
                        if (warningMatch) {
                          const warningText = warningMatch[1];
                          const cleanJustification = justification.replace(/\[WARNING: [^\]]+\]/, '').trim();
                          
                          return (
                            <div className="space-y-2">
                              {/* Warning Alert */}
                              <div className="bg-red-50 border border-red-200 rounded p-2 flex items-start space-x-2">
                                <div className="flex-shrink-0">
                                  <svg className="w-4 h-4 text-red-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-red-800">CITATION MISMATCH DETECTED</p>
                                  <p className="text-xs text-red-700 mt-1">{warningText}</p>
                                </div>
                              </div>
                              {/* Clean Justification */}
                              {cleanJustification && (
                                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                  <p className="text-sm text-gray-700 leading-relaxed">{cleanJustification}</p>
                                </div>
                              )}
                            </div>
                          );
                        } else {
                          return (
                            <div className={`border rounded p-3 ${
                              isGeneralExemption 
                                ? 'bg-orange-50 border-orange-200' 
                                : 'bg-blue-50 border-blue-200'
                            }`}>
                              {isGeneralExemption && (
                                <div className="flex items-center mb-2">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-orange-200 text-orange-800">
                                    General Guidelines
                                  </span>
                                </div>
                              )}
                              <p className="text-sm text-gray-700 leading-relaxed">{justification}</p>
                            </div>
                          );
                        }
                      })()}
                    </div>
                  )}
                  
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => removeRedaction(activeIndex)}
                      className="flex-1 px-3 py-2 text-sm font-medium bg-red-100 text-red-700 border border-red-200 rounded hover:bg-red-200 transition-colors"
                    >
                      Remove
                    </button>
                    <button
                      onClick={() => onSave(redactions)}
                      className="flex-1 px-3 py-2 text-sm font-medium bg-green-600 text-white border border-green-600 rounded hover:bg-green-700 transition-colors"
                    >
                      Save All
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Manual redaction tools */}
            {!pdfPreviewMode && !showApprovalWorkflow && (
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Create New Redaction</h3>
                
                {!selectedExemptionForCreation && (
                  <div>
                    <div className="text-xs text-gray-600 mb-3">
                      Select an exemption type, then drag on the PDF to create a redaction
                    </div>
                    <div className="space-y-3">
                      {/* (b)(1) - National Security */}
                      <div>
                        <div className="text-xs font-medium text-gray-700 mb-1">(b)(1) National Security</div>
                        <div className="grid grid-cols-2 gap-1">
                          <button onClick={() => startRedactionCreation('(b)(1)')} className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors">(b)(1)</button>
                        </div>
                      </div>
                      
                      {/* (b)(2) - Internal Personnel Rules */}
                      <div>
                        <div className="text-xs font-medium text-gray-700 mb-1">(b)(2) Internal Personnel Rules</div>
                        <div className="grid grid-cols-2 gap-1">
                          <button onClick={() => startRedactionCreation('(b)(2)')} className="px-2 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors">(b)(2)</button>
                        </div>
                      </div>
                      
                      {/* (b)(3) - Statutory Exemptions */}
                      <div>
                        <div className="text-xs font-medium text-gray-700 mb-1">(b)(3) Statutory Exemptions</div>
                        <div className="grid grid-cols-2 gap-1">
                          <button onClick={() => startRedactionCreation('(b)(3)')} className="px-2 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors">(b)(3)</button>
                        </div>
                      </div>
                      
                      {/* (b)(4) - Trade Secrets */}
                      <div>
                        <div className="text-xs font-medium text-gray-700 mb-1">(b)(4) Trade Secrets</div>
                        <div className="grid grid-cols-2 gap-1">
                          <button onClick={() => startRedactionCreation('(b)(4)')} className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors">(b)(4)</button>
                        </div>
                      </div>
                      
                      {/* (b)(5) - Deliberative Process */}
                      <div>
                        <div className="text-xs font-medium text-gray-700 mb-1">(b)(5) Deliberative Process</div>
                        <div className="grid grid-cols-2 gap-1">
                          <button onClick={() => startRedactionCreation('(b)(5)')} className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">(b)(5)</button>
                        </div>
                      </div>
                      
                      {/* (b)(6) - Personal Privacy */}
                      <div>
                        <div className="text-xs font-medium text-gray-700 mb-1">(b)(6) Personal Privacy</div>
                        <div className="grid grid-cols-2 gap-1">
                          <button onClick={() => startRedactionCreation('(b)(6)')} className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors">(b)(6)</button>
                        </div>
                      </div>
                      
                      {/* (b)(7) - Law Enforcement */}
                      <div>
                        <div className="text-xs font-medium text-gray-700 mb-1">(b)(7) Law Enforcement</div>
                        <div className="grid grid-cols-2 gap-1">
                          <button onClick={() => startRedactionCreation('(b)(7)(A)')} className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors">(b)(7)(A)</button>
                          <button onClick={() => startRedactionCreation('(b)(7)(B)')} className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors">(b)(7)(B)</button>
                          <button onClick={() => startRedactionCreation('(b)(7)(C)')} className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors">(b)(7)(C)</button>
                          <button onClick={() => startRedactionCreation('(b)(7)(D)')} className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors">(b)(7)(D)</button>
                          <button onClick={() => startRedactionCreation('(b)(7)(E)')} className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors">(b)(7)(E)</button>
                          <button onClick={() => startRedactionCreation('(b)(7)(F)')} className="px-2 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors">(b)(7)(F)</button>
                        </div>
                      </div>
                      
                      {/* (b)(8) - Financial Institutions */}
                      <div>
                        <div className="text-xs font-medium text-gray-700 mb-1">(b)(8) Financial Institutions</div>
                        <div className="grid grid-cols-2 gap-1">
                          <button onClick={() => startRedactionCreation('(b)(8)')} className="px-2 py-1 text-xs bg-pink-600 text-white rounded hover:bg-pink-700 transition-colors">(b)(8)</button>
                        </div>
                      </div>
                      
                      {/* (b)(9) - Geological Information */}
                      <div>
                        <div className="text-xs font-medium text-gray-700 mb-1">(b)(9) Geological Information</div>
                        <div className="grid grid-cols-2 gap-1">
                          <button onClick={() => startRedactionCreation('(b)(9)')} className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors">(b)(9)</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {selectedExemptionForCreation && !isSelecting && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-blue-900">
                        Ready to create: {selectedExemptionForCreation}
                      </div>
                      <button
                        onClick={cancelRedactionCreation}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="text-xs text-green-600 mb-2">
                      Click and drag on the PDF to create this redaction
                    </div>
                    <div className="text-xs text-gray-500">
                      Notice the crosshair cursor on the PDF
                    </div>
                  </div>
                )}

                {selectedExemptionForCreation && isSelecting && (
                  <div>
                    <div className="text-sm font-medium text-blue-900 mb-2">
                      Creating: {selectedExemptionForCreation}
                    </div>
                    <div className="text-xs text-orange-600">
                      Drag to select the area to redact...
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Redaction list */}
            {redactions.length > 0 && !showApprovalWorkflow && (
              <div className="p-6">
                <div className="space-y-3">
                  {redactions.map((redaction, index) => (
                    <div
                      key={redaction.id || index}
                      onClick={() => setSelectedRedactionIndex(index)}
                      className={`
                        p-3 border rounded cursor-pointer text-sm transition-all
                        ${selectedRedactionIndex === index 
                          ? 'border-blue-500 bg-blue-50 shadow-sm' 
                          : 'border-gray-200 hover:bg-gray-50'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-blue-900">
                          {redaction.exemption || '(b)(1)'}
                        </span>
                        <span className={`
                          px-2 py-1 text-xs font-medium rounded-full
                          ${redaction.status === 'approved' ? 'bg-green-100 text-green-800' :
                            redaction.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }
                        `}>
                          {redaction.status || 'pending'}
                        </span>
                      </div>
                      <div className="text-gray-600 line-clamp-2">
                        "{redaction.original_text}"
                      </div>
                      {redaction.confidence && (
                        <div className="text-xs text-gray-500 mt-1">
                          Confidence: {Math.round(redaction.confidence * 100)}%
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {redactions.length === 0 && (
              <div className="p-6 text-center text-gray-500">
                <UserIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No redactions yet.</p>
                <p className="text-sm">Upload a document to start redacting.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rule Details Modal */}
      {ruleModalOpen && selectedRule && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                FOIA Rule Details: {selectedRule.rule_id || 'Unknown ID'}
              </h3>
              <button
                onClick={closeRuleModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            {/* Header Information */}
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {selectedRule.exemption_category && (
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getExemptionBadgeColor(selectedRule.exemption_category.replace(/[()]/g, ''))}`}>
                      {selectedRule.exemption_category}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">Exemption Category</p>
                  </div>
                )}
                {selectedRule.context_domain && (
                  <div className="text-center p-3 bg-indigo-50 rounded-lg">
                    <p className="text-sm font-medium text-indigo-800">{selectedRule.context_domain}</p>
                    <p className="text-xs text-gray-500 mt-1">Domain</p>
                  </div>
                )}
                {selectedRule.source && (
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-sm font-medium text-green-800">{selectedRule.source}</p>
                    <p className="text-xs text-gray-500 mt-1">Source Document</p>
                  </div>
                )}
              </div>
            </div>

            {/* Rule Title */}
            {selectedRule.rule_title && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Rule Title:</h4>
                <p className="text-base font-medium text-gray-900 bg-gray-50 p-3 rounded-md">{selectedRule.rule_title}</p>
              </div>
            )}

            {/* Description */}
            {(selectedRule.rule_description || selectedRule.description) && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Description:</h4>
                <p className="text-sm text-gray-700 bg-blue-50 border border-blue-200 p-3 rounded-md leading-relaxed">
                  {selectedRule.rule_description || selectedRule.description}
                </p>
              </div>
            )}

            {/* Redaction Criteria */}
            {selectedRule.redaction_criteria && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Redaction Criteria:</h4>
                <p className="text-sm text-gray-700 bg-purple-50 border border-purple-200 p-3 rounded-md">
                  {selectedRule.redaction_criteria}
                </p>
              </div>
            )}

            {/* Keywords */}
            {selectedRule.keywords && selectedRule.keywords.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Keywords:</h4>
                <div className="flex flex-wrap gap-1">
                  {selectedRule.keywords.slice(0, 12).map((keyword, idx) => (
                    <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700 border">
                      {keyword}
                    </span>
                  ))}
                  {selectedRule.keywords.length > 12 && (
                    <span className="text-xs text-gray-500 px-2 py-1">
                      +{selectedRule.keywords.length - 12} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Examples */}
            {selectedRule.examples && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Examples:</h4>
                <p className="text-sm text-gray-700 bg-yellow-50 border border-yellow-200 p-3 rounded-md">
                  {selectedRule.examples}
                </p>
              </div>
            )}

            {/* Agency Guidance */}
            {selectedRule.agency_guidance && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Agency Guidance:</h4>
                <p className="text-sm text-gray-700 bg-blue-50 border border-blue-200 p-3 rounded-md">
                  {selectedRule.agency_guidance}
                </p>
              </div>
            )}

            {/* Considerations */}
            {selectedRule.considerations && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Considerations:</h4>
                <p className="text-sm text-gray-700 bg-orange-50 border border-orange-200 p-3 rounded-md">
                  {selectedRule.considerations}
                </p>
              </div>
            )}

            {/* Balancing Test */}
            {selectedRule.balancing_test && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Balancing Test:</h4>
                <p className="text-sm text-gray-700 bg-green-50 border border-green-200 p-3 rounded-md">
                  {selectedRule.balancing_test}
                </p>
              </div>
            )}

            {/* Legal Citation & Context */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {selectedRule.legal_citation && selectedRule.legal_citation !== 'N/A' && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Legal Citation:</h4>
                  <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 p-2 rounded">
                    {selectedRule.legal_citation}
                  </p>
                </div>
              )}
              {selectedRule.context_section && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Context Section:</h4>
                  <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 p-2 rounded">
                    {selectedRule.context_section}
                  </p>
                </div>
              )}
            </div>

            {/* Advanced Options - Collapsible JSON */}
            <div className="border-t pt-4">
              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                  <span>Advanced: View Raw JSON</span>
                  <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500">Complete rule data in JSON format</p>
                    <button
                      onClick={copyRuleJSON}
                      className="text-xs bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700 transition-colors"
                    >
                      Copy JSON
                    </button>
                  </div>
                  <div className="bg-gray-900 text-green-400 p-3 rounded-lg overflow-auto max-h-64">
                    <pre className="text-xs whitespace-pre-wrap font-mono">
                      {JSON.stringify(selectedRule, null, 2)}
                    </pre>
                  </div>
                </div>
              </details>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={closeRuleModal}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .redaction-overlay-pending {
          background-color: rgba(251, 191, 36, 0.4) !important;
          border: 1px solid rgba(251, 191, 36, 0.6) !important;
        }
        
        .redaction-overlay-approved {
          background-color: rgba(16, 185, 129, 0.4) !important;
          border: 1px solid rgba(16, 185, 129, 0.6) !important;
        }
        
        .redaction-overlay-rejected {
          background-color: rgba(239, 68, 68, 0.4) !important;
          border: 1px solid rgba(239, 68, 68, 0.6) !important;
          opacity: 0.5;
        }
        
        .exemption-b1 { border-left: 3px solid #dc2626 !important; }
        .exemption-b6 { border-left: 3px solid #7c3aed !important; }
        .exemption-b7 { border-left: 3px solid #c2410c !important; }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default PDFRedactionEditor;